// ─── Supplier Spec (D27) ────────────────────────────────────────────────────
/** The stages a factory sends a Supplier Spec for: the CNC sample and the production line sample. */
export const SUPPLIER_SPEC_STAGES = ['cnc', 'pls'];
/** The template column each stage's figures come from: CNC the factory's Initial Specs, PLS the Production Line Sample. */
export const SUPPLIER_SPEC_STAGE_COLUMN = {
    cnc: 'initial',
    pls: 'pls',
};
/** The supplier figures a process stage is held against: Back-and-forth evaluates a revised CNC sample, so CNC's. */
export function supplierSpecStageFor(stage) {
    return stage === 'pls' ? 'pls' : 'cnc';
}
/** SSV-000001 */
export const SUPPLIER_SPEC_VERSION_ID_PATTERN = /^SSV-\d{6,}$/;
/** The row key of a Supplier Spec row. */
export function supplierSpecRowKey(row) {
    return `${row.section}|${row.label}|${row.qualifier ?? ''}`;
}
/** The hardware form's power questions, by voltage (field path suffix). */
const POWER_FIELDS = [
    [100, 'power_and_current_100vac_60hz'],
    [120, 'power_and_current_120vac_60hz'],
    [208, 'power_and_current_208vac_60hz'],
    [230, 'power_and_current_230vac_50hz'],
    [240, 'power_and_current_240vac_50hz'],
    [277, 'power_and_current_277vac_60hz'],
];
const measure = (id, label, fieldPath, specFieldKey, specSubField = null, read = 'value', volts) => ({ id, label, formId: 'standard', fieldPath, specFieldKey, specSubField, read, ...(volts ? { volts } : {}) });
/**
 * The curated mapping: hardware-form question → spec field the factory
 * declares, and how to read it (design doc, "Measured mapping"). Field paths
 * are the live `standard` definition's (checked 2026-10-13); spec field keys
 * are the spec library's, as the Supplier Spec reader matches them.
 */
export const EVAL_SPEC_MEASURES = [
    measure('weight', 'Weight', 'weight', 'cp_weight', 'weight'),
    measure('shipping_weight', 'Shipping weight', 'shipping_weight_inside_box_or_roadcase', 'cp_weight_shipping', 'weight'),
    measure('size', 'Size (L × W × H)', 'size_l_x_w_x_h', 'cp_dimensions'),
    measure('shipping_size', 'Shipping size (L × W × H)', 'shipping_size_l_x_w_x_h_box_or_roadcase', 'cp_dimensions_shipping'),
    measure('dmx_channels', 'DMX channels', 'dmx_channels', 'ctrl_dmx_channels', 'channels'),
    ...POWER_FIELDS.flatMap(([volts, path]) => [
        measure(`power_${volts}`, `Power at ${volts} V`, path, 'elec_power_table', 'watts', 'power_watts', volts),
        measure(`current_${volts}`, `Current at ${volts} V`, path, 'elec_power_table', 'amps', 'power_amps', volts),
        measure(`pf_${volts}`, `Power factor at ${volts} V`, path, 'elec_power_table', 'power_factor', 'power_pf', volts),
    ]),
    measure('input_voltage', 'Input voltage', 'input_voltage', 'elec_input_voltage'),
    measure('fuse', 'Fuse or breaker', 'fuse_or_breaker_rating', 'ss_fuse'),
    measure('linking_120', 'Power linking max units at 120 V', 'power_linking_max_units', 'elec_power_linking', 'max_units', 'linking', 120),
    measure('linking_230', 'Power linking max units at 230 V', 'power_linking_max_units', 'elec_power_linking', 'max_units', 'linking', 230),
    measure('pwm', 'PWM / LED refresh rate', 'led_refresh_rate', 'opt_pwm_frequency'),
    measure('strobe', 'Strobe speed', 'strobe_speed', 'de_strobe_rate'),
    measure('pan_tilt', 'Pan & tilt', 'pan_tilt', 'de_pan_tilt_ranges', 'range'),
    measure('noise_reset', 'Noise (max during reset)', 'maxDbDuringReset', 'cert_noise_levels', 'level'),
];
/** The measures a form's question feeds (one question can feed several: power, current and PF at a voltage). */
export function evalSpecMeasuresFor(formId, fieldPath) {
    return EVAL_SPEC_MEASURES.filter((m) => m.formId === formId && m.fieldPath === fieldPath);
}
/** Out of tolerance beyond this many percent unless the review sets another figure for the item. */
export const EVAL_SPEC_DEFAULT_TOLERANCE_PCT = 5;
// ─── PM Spec Review (D28) ───────────────────────────────────────────────────
/** A review row's decision: Confirm (it matches, or the difference is accepted), raised as a point, or not measured. */
export const EVAL_SPEC_ITEM_STATUSES = ['confirmed', 'flagged', 'not_measured'];
/** An engineer's flag on a measured figure, for the PM (D29). */
export const EVAL_SPEC_FLAG_STATUSES = ['open', 'confirmed', 'raised', 'dismissed', 'withdrawn'];
/** SFL-000001 */
export const EVAL_SPEC_FLAG_ID_PATTERN = /^SFL-\d{6,}$/;
/**
 * Whether PM Spec Review may be Completed (D28, D29): every row with both
 * figures confirmed or raised, and no engineer flag left open.
 */
export function evalSpecReviewCompletion(rows, flags) {
    const undecided = rows.filter((r) => r.needsDecision).map((r) => r.itemId);
    const openFlags = flags.filter((f) => f.status === 'open').length;
    return { canComplete: undecided.length === 0 && openFlags === 0, undecided, openFlags };
}
/** "Weight: supplier 32.3 kg, measured 27.3 kg (−15%)": a raised point's title. */
export function evalSpecPointTitle(label, supplier, measured, deltaPct) {
    const delta = deltaPct === null ? '' : ` (${deltaPct > 0 ? '+' : deltaPct < 0 ? '−' : ''}${Math.abs(Math.round(deltaPct * 10) / 10)}%)`;
    return `${label}: supplier ${supplier ?? '—'}, measured ${measured ?? '—'}${delta}`.slice(0, 200);
}
/** A figure as text: "32.3 kg", "1200 / 2400 Hz". */
export function supplierSpecNumbersText(numbers) {
    return numbers.map((n) => `${n.value}${n.unit ? (n.unit === '°' || n.unit === '%' ? n.unit : ` ${n.unit}`) : ''}`).join(' / ');
}
//# sourceMappingURL=evaluation-spec.js.map